import React, { useState, useEffect } from 'react';
import {
  CreditCardIcon,
  MagnifyingGlassIcon,
  PlusIcon,
  ArrowDownTrayIcon,
  FunnelIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
} from '@heroicons/react/24/outline';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import Button from '../../components/UI/Button';
import DataTable, { TableColumn, TableAction } from '../../components/UI/DataTable';
import Pagination from '../../components/UI/Pagination';
import StatsCard from '../../components/UI/StatsCard';
import PaymentModal from '../../components/Payments/PaymentModal';
import AdvancedSearch from '../../components/UI/AdvancedSearch';
import BulkActions, { commonBulkActions } from '../../components/UI/BulkActions';
import { paymentService } from '../../services/paymentService';
import { PaymentDto } from '../../types/payment';
import DeleteConfirmModal from '../../components/UI/DeleteConfirmModal';
import { clientService } from '../../services/clientService';
import { receiptService } from '../../services/receiptService';
import { useNavigate } from 'react-router-dom';
import { DocumentTextIcon } from '@heroicons/react/24/outline';
import { toast } from 'react-toastify';

interface Payment {
  id: string;
  clientId: string;
  clientName: string;
  amount: number;
  currency: string;
  status: 'PENDING' | 'COMPLETED' | 'FAILED' | 'REFUNDED';
  paymentMethod: string;
  transactionId?: string;
  description?: string;
  dueDate: string;
  paidDate?: string;
  createdAt: string;
  updatedAt: string;
}

const PaymentsPage: React.FC = () => {
  const [selectedPayments, setSelectedPayments] = useState<string[]>([]);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [totalItems, setTotalItems] = useState(0);
  const queryClient = useQueryClient();

  // Search and filters
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [clientFilter, setClientFilter] = useState('');
  const [isAdvancedSearchOpen, setIsAdvancedSearchOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<Payment | null>(null);

  // Modals
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [editingPayment, setEditingPayment] = useState<Payment | null>(null);

  // Load payments from API
  const { data, isLoading, error } = useQuery({
    queryKey: ['payments', currentPage, itemsPerPage, searchTerm, statusFilter, clientFilter],
    queryFn: () => paymentService.getPayments({ page: currentPage, limit: itemsPerPage, search: searchTerm || undefined, status: statusFilter || undefined, clientId: clientFilter || undefined }),
  });

  const payments: Payment[] = (data?.payments || []).map((p: PaymentDto) => ({
    id: p.id,
    clientId: p.clientId,
    clientName: p.client ? `${p.client.firstName} ${p.client.lastName}` : '',
    amount: p.amount,
    currency: p.currency,
    status: p.status as Payment['status'],
    paymentMethod: p.method,
    transactionId: p.reference,
    description: p.notes,
    dueDate: p.paymentDate,
    paidDate: undefined,
    createdAt: p.createdAt,
    updatedAt: p.updatedAt,
  }));

  useEffect(() => {
    if (data?.pagination) {
      setTotalItems(data.pagination.total);
    }
  }, [data]);

  // Stats API
  const { data: statsDataResp } = useQuery({
    queryKey: ['payments','stats', searchTerm, statusFilter, clientFilter],
    queryFn: () => paymentService.getStats({
      search: searchTerm || undefined,
      status: statusFilter || undefined,
      clientId: clientFilter || undefined,
    }),
  } as any);
  const statsApi = (statsDataResp as any)?.stats;

  // Stats data
  const statsData = [
    {
      id: 'total-payments',
      title: 'Total Payments',
      value: statsApi?.totalPayments ?? payments.length,
      icon: <CreditCardIcon className="w-6 h-6" />,
      color: 'blue' as const,
    },
    {
      id: 'completed-payments',
      title: 'Completed',
      value: statsApi?.byStatus?.COMPLETED?.count ?? payments.filter(p => p.status === 'COMPLETED').length,
      icon: <CheckCircleIcon className="w-6 h-6" />,
      color: 'green' as const,
    },
    {
      id: 'pending-payments',
      title: 'Pending',
      value: statsApi?.byStatus?.PENDING?.count ?? payments.filter(p => p.status === 'PENDING').length,
      icon: <ClockIcon className="w-6 h-6" />,
      color: 'yellow' as const,
    },
    {
      id: 'failed-payments',
      title: 'Failed',
      value: statsApi?.byStatus?.FAILED?.count ?? payments.filter(p => p.status === 'FAILED').length,
      icon: <XCircleIcon className="w-6 h-6" />,
      color: 'red' as const,
    },
  ];

  // Clients for advanced search dropdown
  const { data: clientsData } = useQuery({
    queryKey: ['clients','for-select'],
    queryFn: () => clientService.getClients({ page: 1, limit: 1000 }),
  } as any);
  const clientOptions = (((clientsData as any)?.clients) || []).map((c: any) => ({ value: c.id, label: `${c.firstName} ${c.lastName}` }));
  // Mutations for create/update
  const createMutation = useMutation({
    mutationFn: (payload: any) => paymentService.createPayment(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['payments'] });
      setIsPaymentModalOpen(false);
      setEditingPayment(null);
    },
  });

  const updateMutation = useMutation({
    mutationFn: (vars: { id: string; data: any }) => paymentService.updatePayment(vars.id, vars.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['payments'] });
      setIsPaymentModalOpen(false);
      setEditingPayment(null);
    },
  });
  const deleteMutation = useMutation({
    mutationFn: (id: string) => paymentService.deletePayment(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['payments'] });
      setConfirmDelete(null);
      setSelectedPayments([]);
      toast.success('Payment deleted successfully');
    },
    onError: (error: any) => {
      const errorMessage = error?.message || 'Failed to delete payment';
      toast.error(errorMessage);
      setConfirmDelete(null);
    },
  });

  const loading = isLoading;
  const errorText = error ? 'Failed to load payments' : undefined;


  const getStatusIcon = (status: Payment['status']) => {
    switch (status) {
      case 'COMPLETED':
        return <CheckCircleIcon className="w-4 h-4 text-green-500" />;
      case 'PENDING':
        return <ClockIcon className="w-4 h-4 text-yellow-500" />;
      case 'FAILED':
        return <XCircleIcon className="w-4 h-4 text-red-500" />;
      case 'REFUNDED':
        return <XCircleIcon className="w-4 h-4 text-gray-500" />;
    }
  };

  const getStatusBadge = (status: Payment['status']) => {
    const baseClasses = "inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium";
    switch (status) {
      case 'COMPLETED':
        return `${baseClasses} bg-green-100 text-green-800`;
      case 'PENDING':
        return `${baseClasses} bg-yellow-100 text-yellow-800`;
      case 'FAILED':
        return `${baseClasses} bg-red-100 text-red-800`;
      case 'REFUNDED':
        return `${baseClasses} bg-gray-100 text-gray-800`;
    }
  };

  const columns: TableColumn<Payment>[] = [
    {
      key: 'id',
      header: 'Payment ID',
      width: '120px',
      render: (value) => (
        <span className="font-mono text-sm text-gray-600">{value}</span>
      ),
    },
    {
      key: 'clientName',
      header: 'Client',
      sortable: true,
    },
    {
      key: 'amount',
      header: 'Amount',
      sortable: true,
      render: (value, row) => (
        <span className="font-semibold">${value} {row.currency}</span>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      sortable: true,
      render: (value) => (
        <div className="flex items-center space-x-2">
          {getStatusIcon(value)}
          <span className={getStatusBadge(value)}>{value}</span>
        </div>
      ),
    },
    {
      key: 'paymentMethod',
      header: 'Method',
      sortable: true,
    },
    {
      key: 'dueDate',
      header: 'Due Date',
      sortable: true,
      render: (value) => new Date(value).toLocaleDateString(),
    },
    {
      key: 'paidDate',
      header: 'Paid Date',
      render: (value) => value ? new Date(value).toLocaleDateString() : '-',
    },
  ];

  const navigate = useNavigate();

  const handleGenerateReceipt = async (payment: Payment) => {
    try {
      console.log('Generating receipt for payment:', payment.id);

      // Try to get existing receipt first
      let receipt = await receiptService.getReceiptByPaymentId(payment.id);

      if (receipt) {
        console.log('Receipt already exists:', receipt.id);
        toast.info('Opening existing receipt...');
      } else {
        console.log('Creating new receipt...');
        // Generate new receipt
        receipt = await receiptService.generateReceipt(payment.id);
        console.log('Receipt created:', receipt.id);
        toast.success('Receipt generated successfully');
      }

      // Open the receipt for printing
      console.log('Opening receipt for printing...');
      await receiptService.printReceipt(receipt.id);

    } catch (error: any) {
      console.error('Error generating receipt:', error);
      console.error('Error details:', {
        message: error?.message,
        response: error?.response?.data,
        status: error?.response?.status,
      });
      const errorMessage = error?.response?.data?.message || error?.message || 'Failed to generate receipt';
      toast.error(errorMessage);
    }
  };

  const handleViewReceipt = async (payment: Payment) => {
    try {
      // Check if receipt exists
      const existingReceipt = await receiptService.getReceiptByPaymentId(payment.id);

      if (existingReceipt) {
        // Open receipt in new window for viewing/printing
        await receiptService.printReceipt(existingReceipt.id);
      } else {
        toast.info('No receipt found. Click "Generate Receipt" to create one.');
      }
    } catch (error: any) {
      console.error('Error viewing receipt:', error);
      toast.error('Failed to view receipt');
    }
  };

  const actions: TableAction<Payment>[] = [
    {
      key: 'view',
      label: 'View',
      onClick: (payment) => {
        console.log('View button clicked', payment);
        setEditingPayment(payment);
        setIsPaymentModalOpen(true);
      },
    },
    {
      key: 'generate-receipt',
      label: 'Generate Receipt',
      icon: <DocumentTextIcon className="w-4 h-4" />,
      variant: 'primary',
      onClick: (payment) => {
        console.log('Generate Receipt button clicked', payment);
        handleGenerateReceipt(payment);
      },
      show: (payment: Payment) => payment.status === 'COMPLETED' || payment.status === 'PENDING',
    },
    {
      key: 'edit',
      label: 'Edit',
      onClick: (payment) => {
        console.log('Edit button clicked', payment);
        setEditingPayment(payment);
        setIsPaymentModalOpen(true);
      },
    },
    {
      key: 'delete',
      label: 'Delete',
      variant: 'danger',
      onClick: (payment) => {
        console.log('Delete button clicked', payment);
        setConfirmDelete(payment);
      },
    },
  ];

  const bulkActions = [
    commonBulkActions.delete,
    {
      key: 'mark-paid',
      label: 'Mark as Paid',
      icon: <CheckCircleIcon className="w-4 h-4" />,
      variant: 'success' as const,
      requiresConfirmation: true,
      confirmationTitle: 'Mark Payments as Paid',
      confirmationMessage: 'Are you sure you want to mark the selected payments as paid?',
    },
    {
      key: 'send-reminder',
      label: 'Send Reminder',
      icon: <CreditCardIcon className="w-4 h-4" />,
      variant: 'primary' as const,
    },
  ];

  const handleBulkAction = async (actionKey: string, selectedIds: string[]) => {
    if (actionKey === 'delete') {
      for (const id of selectedIds) {
        await deleteMutation.mutateAsync(id);
      }
    }
  };

  const handleExport = async () => {
    try {
      const blob = await paymentService.exportPayments({ search: searchTerm || undefined, status: statusFilter || undefined, clientId: clientFilter || undefined });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `payments-${new Date().toISOString().slice(0,10)}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      console.error('Export failed', e);
    }
  };

  const advancedSearchFields = [
    {
      key: 'search',
      label: 'Search',
      type: 'text' as const,
      placeholder: 'Search by client name, payment ID, or transaction ID',
    },
    {
      key: 'clientId',
      label: 'Client',
      type: 'select' as const,
      options: clientOptions,
    },
    {
      key: 'status',
      label: 'Status',
      type: 'select' as const,
      options: [
        { value: 'PENDING', label: 'Pending' },
        { value: 'COMPLETED', label: 'Completed' },
        { value: 'FAILED', label: 'Failed' },
        { value: 'REFUNDED', label: 'Refunded' },
      ],
    },
    {
      key: 'paymentMethod',
      label: 'Payment Method',
      type: 'select' as const,
      options: [
        { value: 'Credit Card', label: 'Credit Card' },
        { value: 'Bank Transfer', label: 'Bank Transfer' },
        { value: 'PayPal', label: 'PayPal' },
        { value: 'Cash', label: 'Cash' },
      ],
    },
    {
      key: 'dateRange',
      label: 'Date Range',
      type: 'daterange' as const,
    },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Payments</h1>
          <p className="text-gray-600">Manage client payments and transactions</p>
        </div>
        <div className="flex items-center space-x-3">
          <Button
            variant="secondary"
            onClick={handleExport}
            className="flex items-center"
          >
            <ArrowDownTrayIcon className="w-4 h-4 mr-2" />
            Export
          </Button>
          <Button
            onClick={() => {
              setEditingPayment(null);
              setIsPaymentModalOpen(true);
            }}
            className="flex items-center"
          >
            <PlusIcon className="w-4 h-4 mr-2" />
            Add Payment
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <StatsCard stats={statsData} />

      {/* Search and Filters */}
      <div className="bg-white rounded-lg shadow-soft p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-gray-900">Payment Records</h3>
          <div className="flex items-center space-x-3">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search payments..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="form-input pl-10 pr-4 py-2 w-64"
              />
            </div>
            <Button
              variant="secondary"
              onClick={() => setIsAdvancedSearchOpen(true)}
              className="flex items-center"
            >
              <FunnelIcon className="w-4 h-4 mr-2" />
              Advanced Search
            </Button>
          </div>
        </div>

        <DataTable
          columns={columns}
          data={payments}
          loading={loading}
          error={errorText}
          selectable
          selectedItems={selectedPayments}
          onSelectionChange={setSelectedPayments}
          actions={actions}
          emptyMessage="No payments found"
        />

      {confirmDelete && (
        <DeleteConfirmModal
          title="Delete Payment"
          message={`Are you sure you want to delete payment ${confirmDelete.id}? This action cannot be undone.`}
          onCancel={() => setConfirmDelete(null)}
          onConfirm={() => deleteMutation.mutate(confirmDelete.id)}
          loading={deleteMutation.isPending}
        />
      )}

        <Pagination
          currentPage={currentPage}
          totalPages={Math.ceil(totalItems / itemsPerPage)}
          totalItems={totalItems}
          itemsPerPage={itemsPerPage}
          onPageChange={setCurrentPage}
          onItemsPerPageChange={setItemsPerPage}
        />
      </div>

      {/* Bulk Actions */}
      <BulkActions
        selectedItems={selectedPayments}
        totalItems={totalItems}
        actions={bulkActions}
        onAction={handleBulkAction}
        onSelectAll={() => setSelectedPayments(payments.map(p => p.id))}
        onClearSelection={() => setSelectedPayments([])}
        isVisible={selectedPayments.length > 0}
      />

      {/* Modals */}
      <PaymentModal
        isOpen={isPaymentModalOpen}
        onClose={() => {
          setIsPaymentModalOpen(false);
          setEditingPayment(null);
        }}
        payment={editingPayment}
        onSave={(p) => {
          const payload: any = {
            clientId: p.clientId,
            amount: p.amount,
            currency: p.currency,
            method: p.paymentMethod,
            transactionId: p.transactionId,
            notes: p.description,
            status: p.status,
          };

          // Map paidDate or dueDate to paidAt
          const paidAtDate = p.paidDate || p.dueDate;
          if (paidAtDate) {
            payload.paidAt = paidAtDate;
          }

          if (editingPayment?.id) {
            updateMutation.mutate({ id: editingPayment.id, data: payload });
          } else {
            createMutation.mutate(payload);
          }
        }}
      />

      <AdvancedSearch
        fields={advancedSearchFields}
        filters={{ search: searchTerm, status: statusFilter || undefined, clientId: clientFilter || undefined }}
        onFiltersChange={(f: any) => { if ('search' in f) setSearchTerm(f.search || ''); if ('status' in f) setStatusFilter(f.status || ''); if ('clientId' in f) setClientFilter(f.clientId || ''); }}
        onSearch={() => setIsAdvancedSearchOpen(false)}
        onClear={() => { setSearchTerm(''); setStatusFilter(''); setClientFilter(''); }}
        isOpen={isAdvancedSearchOpen}
        onClose={() => setIsAdvancedSearchOpen(false)}
      />
    </div>
  );
};

export default PaymentsPage;
